import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'

import { DrmJednotkaResponse, PROPERTY_TYPES } from '../../../common/drmTypes'
import { PROPERTY_TAB_KEYS } from '../../../common/propertyDetailTypes'
import {
  getVKByUnitArea,
  rentAttributePriceByVK,
  rentAttributePriceByVKNewBuild,
  RentCoverage,
} from '../../../common/propertyPriceTypes'
import { useAppSelector } from '../../../redux/hooks'
import { useFetchPropertyPrice } from '../../utils/useFetchPropertyPrice'
import { Money } from '../Money/Money'
import { PropertyDetailLink } from '../PropertyDetailLink/PropertyDetailLink'
import { PropertyDetailSidebarPriceUnitCoverage } from '../PropertyDetailSidebarPriceUnitCoverage/PropertyDetailSidebarPriceUnitCoverage'

export interface PropertyDetailSidebarPriceUnitProps {
  disableFetch?: boolean
  isDrawer?: boolean
}

const unknownData = (
  <div className="text-gray-400 font-normal text-left">Nezjištěno</div>
)

const moneyProps = {
  decimalNumbers: 0,
  className: 'whitespace-nowrap block',
  grayscale: true,
  noData: unknownData,
}

export const PropertyDetailSidebarPriceUnit: FunctionComponent<PropertyDetailSidebarPriceUnitProps> =
  ({ disableFetch }) => {
    const priceData = useAppSelector(state => state.property.price.data)
    const unitArea = useAppSelector(
      state => state.property.price.data?.attributes?.unitAreaM2
    )
    const drmDetail = useAppSelector(
      state => state.property.drm.data as DrmJednotkaResponse
    )
    const fetchPropertyPrice = useFetchPropertyPrice()

    const [averageRentM2, setAverageRentM2] = useState<number | null>(null)

    useEffect(() => {
      if (disableFetch || !drmDetail?.id) return
      if (drmDetail.id === priceData?.propertyId) return

      fetchPropertyPrice({
        propertyId: drmDetail.id,
        propertyType: PROPERTY_TYPES.UNIT,
      })
    }, [disableFetch, drmDetail?.id, fetchPropertyPrice, priceData?.propertyId])

    const unitRent = useMemo(() => {
      const isNewBuild = !!priceData?.attributes?.isNewBuild
      const isNonPanelOrBrick = !!priceData?.attributes?.isNonPanelOrBrick

      if (!priceData?.rentReal) return null

      if (!unitArea) {
        const vkValues = Object.values(priceData.rentReal.vk)
        const averageRentM2 =
          vkValues.reduce((sum, vk) => sum + (vk.reference || 0), 0) /
          vkValues.length

        setAverageRentM2(averageRentM2 || null)
        return null
      }

      const vk = getVKByUnitArea(unitArea)
      const rent = priceData?.rentReal.vk[vk]

      const deltaPrices = isNewBuild
        ? rentAttributePriceByVKNewBuild[vk]
        : rentAttributePriceByVK[vk]

      let perM2Delta = 0
      if (priceData?.attributes?.hasElevator) perM2Delta += deltaPrices.elevator
      if (isNewBuild && isNonPanelOrBrick)
        perM2Delta += rentAttributePriceByVKNewBuild[vk].otherMaterial

      const perM2 = (rent.reference || 0) + perM2Delta
      const total = perM2 && unitArea ? perM2 * unitArea : undefined

      return {
        vk,
        perM2,
        total: total,
        coverage: rent.dataCoverage as RentCoverage | 0,
      }
    }, [
      priceData?.attributes?.hasElevator,
      priceData?.attributes?.isNewBuild,
      priceData?.attributes?.isNonPanelOrBrick,
      priceData?.rentReal,
      unitArea,
    ])

    const unitSale = useMemo(() => {
      const match = priceData?.saleOffer?.localities.find(
        locality => locality.name === drmDetail?.katastralniUzemi?.nazev
      )
      const locality = match || priceData?.saleOffer?.mainLocality
      const perM2 = locality?.averagePriceM2
      const total = perM2 && unitArea ? perM2 * unitArea : undefined

      return {
        coverage: locality?.transactionsCount || 0,
        perM2,
        total,
      }
    }, [
      drmDetail?.katastralniUzemi?.nazev,
      priceData?.saleOffer?.localities,
      priceData?.saleOffer?.mainLocality,
      unitArea,
    ])

    return (
      <>
        <div className="flex">
          <div className="w-1/3">Výměra jednotky:</div>
          <div className="w-2/3 flex justify-between pr-2">
            <Money
              className="font-semibold"
              symbol="m²"
              decimalNumbers={0}
              amount={unitArea}
              noData={unknownData}
            />
            <PropertyDetailLink
              text="Cenové údaje"
              propertyId={drmDetail?.id}
              tab={PROPERTY_TAB_KEYS.PRICE}
              propertyType={PROPERTY_TYPES.UNIT}
            />
          </div>
        </div>

        <div className="table w-full">
          <div className="table-row">
            <div className="table-cell w-1/3">Nabídková cena:</div>
            <div className="table-cell text-right font-semibold w-0">
              <Money amount={unitSale?.total} symbol="Kč" {...moneyProps} />
            </div>
            <div className="table-cell text-right font-semibold w-0">
              <Money
                amount={unitSale?.perM2}
                symbol="Kč/m²"
                {...moneyProps}
                noData={null}
              />
            </div>
            <div className="table-cell text-right font-semibold pr-2">
              <PropertyDetailSidebarPriceUnitCoverage
                saleCoverage={unitSale?.coverage}
                propertyId={drmDetail?.id}
              />
            </div>
          </div>

          <div className="table-row">
            <div className="table-cell w-1/3 pt-2">Tržní nájem:</div>
            <div className="table-cell text-right font-semibold w-0">
              <Money amount={unitRent?.total} symbol="Kč" {...moneyProps} />
            </div>
            <div className="table-cell text-right font-semibold w-1/3 pt-2">
              <Money
                amount={unitRent?.perM2 || averageRentM2}
                symbol="Kč/m²"
                {...moneyProps}
                noData={null}
              />
            </div>
            <div className="table-cell text-right font-semibold pr-2 pt-2">
              <PropertyDetailSidebarPriceUnitCoverage
                rentCoverage={unitRent?.coverage}
                noArea={!unitArea && !!averageRentM2}
                propertyId={drmDetail?.id}
              />
            </div>
          </div>
        </div>
      </>
    )
  }
